/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 *
 */

/**
 * @module bulb-media-embed/bulbmediaembedediting
 */

import {
  Plugin, Widget, toWidget, ViewDowncastWriter, ViewContainerElement,
} from 'ckeditor5';

import { iframely } from '@iframely/embed.js';
import { InsertBulbMediaEmbedCommand } from './bulbmediaembedcommand';
import {
  getBlockVersion, loadIframeWithPDFEmbed, setOverlay, updateIframeSrcAttributes,
} from '../utils';
// Importing Iframely's embed.js as library.

/**
 * The bulb media embed editing infrastructure, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbMediaEmbedEditing extends Plugin {

  /**
   * @inheritDoc
   *
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbMediaEmbedEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    this.defineSchema();
    this.defineConverters();
    this.editor.commands.add('insertEmbed', new InsertBulbMediaEmbedCommand(this.editor));
  }

  /**
   * Method that defines the schema four our embed media block.
   *
   * @private
   */
  private defineSchema(): void {
    const { schema } = this.editor.model;
    schema.register('embed', {
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowAttributes: ['bulb-block-id', 'blockAlignment', 'width', 'bulb-block-version'],
    });
  }

  /**
   * Method that creates and initializes the downcast ( editing and data ) and
   * upcast converters for our embed media block.
   *
   * @private
   */
  private defineConverters(): void {
    const { conversion } = this.editor;
    const { editor } = this;
    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'embed',
      },
      model: (viewElement, { writer: modelWriter }) => modelWriter.createElement('embed', {
        'bulb-block-id': viewElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, viewElement),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'embed',
      view: (modelElement, { writer: viewWriter }) => viewWriter.createContainerElement('figure', {
        class: 'embed',
        'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, modelElement),
      }),
    });

    /**
     * Adds Overlay to the youtube embed.
     * @param viewWriter current view writer.
     * @param contentBlockId content block id.
     * @param figure generated embed figure.
     */
    function addOverlayToEmbed(viewWriter: ViewDowncastWriter, contentBlockId: string, figure: ViewContainerElement): void {
      const overlay = viewWriter.createUIElement('div', {
        class: 'overlay',
        id: `overlay-${contentBlockId}`,
      }, function (domDocument) {
        const domElement = this.toDomElement(domDocument);
        (document as any).editor = editor;
        document.addEventListener('click', setOverlay);
        return domElement;
      });

      viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), overlay);
    }

    conversion.for('editingDowncast').elementToElement({
      model: 'embed',
      view: (modelElement, { writer: viewWriter }) => {
        const contentBlockId = modelElement.getAttribute('bulb-block-id') as string;
        const contentBlock = CKEBulbData.page.unit.contentBlocks[contentBlockId];
        if (contentBlock) {
          const bulbBlockResizeContainer = viewWriter.createContainerElement('div', {
            class: 'resize-overlay',
            id: `resize-${contentBlockId}`,
          });
          const iframelyWrapper = viewWriter.createUIElement('div', {
            class: 'bulb_iframely_wrapper',
          }, function (domDocument) {
            const domElement = this.toDomElement(domDocument);
            domElement.innerHTML = contentBlock.html;
            // Do a slight timeout and then manually load all URLs.
            setTimeout(() => {
              iframely.load();
              loadIframeWithPDFEmbed();
              updateIframeSrcAttributes();
              CKEBulbData.service.oneDrive.loadOneDriveFileEmbed(
                contentBlock,
                CKEBulbData.page.unit.id,
              );
            });
            return domElement;
          });

          const figure = viewWriter.createContainerElement('figure', {
            class: 'embed no-editor-placeholder',
            'bulb-block-id': contentBlockId,
            'bulb-block-version': modelElement.getAttribute('bulb-block-version') || 0,
          });

          viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), bulbBlockResizeContainer);
          viewWriter.insert(viewWriter.createPositionAt(figure, 'end'), iframelyWrapper);

          if (CKEBulbData.page.editing) {
            addOverlayToEmbed(viewWriter, contentBlockId, figure);
          }

          viewWriter.setCustomProperty('embed', true, figure);
          return toWidget(figure, viewWriter, { label: 'Embed Block', hasSelectionHandle: true });
        }
      },
    });
  }

  /**
   * @inheritDoc
   */
  public destroy(): void {
    document.removeEventListener('click', setOverlay);
  }
}
